import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { combineLatest, Observable } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';

import { BaseComponent } from '../../../core/abstractions/base.component';
import { ChangeEmailPhoneModel } from '../../../models/core/change-email-phone.model';
import { DeliveryMethodModel } from '../../../models/delivery-method.model';
import { DeliveryMethodEnum } from '../../../models/enums/delivery-method.enum';
import { OptionalType } from '../../../models/types/optional.type';
import { AuthService } from '../../../services/auth.service';
import { BasicDataService } from '../../../services/basic-data.service';
import { DialogService } from '../../../services/dialog.service';
import { ShareholdersResource } from '../../../services/resources/shareholders.resource';
import { SharedBusinessLogicService } from '../../../services/shared-business-logic.service';
import { SettingsSessionStorage } from '../../../services/storage/settings.session-storage';
import { UserSessionService } from '../../../services/user-session.service';
import { ChangeEmailPhoneComponent } from '../../dialogs/change-email-phone/change-email-phone.component';

@Component({
    selector: 'sl-delivery-method',
    templateUrl: './delivery-method.component.html',
    styleUrls: ['./delivery-method.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DeliveryMethodComponent extends BaseComponent implements OnInit {
    readonly DeliveryMethodEnum = DeliveryMethodEnum;

    readonly deliveryMethod = signal<OptionalType<DeliveryMethodModel>>(undefined);
    readonly currentDeliveryMethodId = signal<OptionalType<DeliveryMethodEnum>>(undefined);
    readonly isChanging = signal(false);

    private canChangeDeliveryMethod = false;
    private shouldSkipMobileValidation = false;

    private readonly basicDataService = inject(BasicDataService);
    private readonly matDialog = inject(MatDialog);
    private readonly shareholdersResource = inject(ShareholdersResource);
    private readonly authService = inject(AuthService);
    private readonly sharedBusinessLogicService = inject(SharedBusinessLogicService);
    private readonly settingsSessionStorage = inject(SettingsSessionStorage);
    private readonly userSessionService = inject(UserSessionService);
    private readonly dialogService = inject(DialogService);

    ngOnInit(): void {
        this.canChangeDeliveryMethod = this.authService.hasPermission('UpdateDeliveryMethodCommand');
        const sblsSub = this.sharedBusinessLogicService.onReloadDeliveryInformation
            .pipe(switchMap(() => this.fetchCurrentDeliveryMethod()))
            .subscribe();

        combineLatest([this.basicDataService.get(), this.fetchCurrentDeliveryMethod()]).subscribe(([basicData]) => {
            this.shouldSkipMobileValidation = basicData.mobileConfirmationDisabled;

            // encourage to switch to electronic delivery method based on setting
            // only encourage once per session, until user logs out
            const hasEncouragedAlready = this.settingsSessionStorage.getHasEncouragedElectronicDeliveryMethod();
            if (
                this.currentDeliveryMethodId() !== DeliveryMethodEnum.Electronic
                && basicData.encourageElectronicDeliveryMethod
                && !hasEncouragedAlready
            ) {
                if (!!this.userSessionService.termsDialogRef) {
                    this.userSessionService.termsDialogRef.afterClosed().subscribe(() => {
                        this.askChangeDeliveryMethod();
                    });
                } else {
                    this.askChangeDeliveryMethod();
                }
            }

            this.settingsSessionStorage.saveHasEncouragedElectronicDeliveryMethod(true);
        });

        this.addSubscriptions(sblsSub);
    }

    changeDeliveryMethod(): void {
        const deliveryMethod = this.deliveryMethod();
        if (!this.canChangeDeliveryMethod || !deliveryMethod) {
            return;
        }

        this.isChanging.set(true);

        if (this.currentDeliveryMethodId() !== DeliveryMethodEnum.Electronic) {
            // Both the email and mobile (if required) should be confirmed, if not we need to start the confirmation process.

            if (!deliveryMethod.emailConfirmed || (!deliveryMethod.mobileNumberConfirmed && !this.shouldSkipMobileValidation)) {
                const dialogRef = this.matDialog.open(ChangeEmailPhoneComponent, {
                    minWidth: '30%',
                    data: new ChangeEmailPhoneModel({
                        isEmailConfirmed: deliveryMethod.emailConfirmed,
                        isPhoneConfirmed: deliveryMethod.mobileNumberConfirmed,
                        validateEmailOnly: this.shouldSkipMobileValidation
                    })
                });

                dialogRef.afterClosed().subscribe(isDone => {
                    if (isDone) {
                        this.fetchCurrentDeliveryMethod().subscribe(() => {
                            this.changeDeliveryMethod();
                        });
                    }
                });

                return;
            }
        }

        this.shareholdersResource
            .setDeliveryMethodForCurrent(
                this.currentDeliveryMethodId() !== DeliveryMethodEnum.Electronic ? DeliveryMethodEnum.Electronic : DeliveryMethodEnum.Paper
            )
            .pipe(
                switchMap(() => this.fetchCurrentDeliveryMethod()),
                finalize(() => this.isChanging.set(false))
            )
            .subscribe();
    }

    private askChangeDeliveryMethod(): void {
        this.dialogService.showGeneralPromptDialog(
            {
                promptKey: 'DeliveryMethod.EncourageElectronicTitle',
                messageKey: 'DeliveryMethod.EncourageElectronicText'
            },
            yes => yes && this.changeDeliveryMethod()
        );
    }

    private fetchCurrentDeliveryMethod(): Observable<void> {
        return this.shareholdersResource.getDeliveryMethodForCurrent().pipe(
            tap(r => {
                this.deliveryMethod.set(r.value);
                this.currentDeliveryMethodId.set(r.value.deliveryMethodId);
                this.isChanging.set(false);
            }),
            map(() => undefined)
        );
    }
}
